/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import UserNotificationDialog from '@ion/dls/src/notifications/UserNotificationDialog';

export type CanRequestConfirmAction = 'retry' | 'cancel';

export interface CanRequestConfirmDialogProps {
  action: CanRequestConfirmAction;
  show: boolean;
  title: string;
  message: string;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const ACTION_LABEL_KEYS: Record<CanRequestConfirmAction, {action: string; dismiss: string;}> = {
  retry : {
    action  : 'common:retry',
    dismiss : 'common:cancel'
  },
  cancel : {
    action  : 'common:editors.shared.yesContinue',
    dismiss : 'common:editors.shared.neverMind'
  }
};

/**
 * Renders the confirmation prompt shared by the CAN request retry and cancel
 * row actions on both CAN requests pages.
 *
 * ```typescript
 * import CanRequestConfirmDialog from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestConfirmDialog';
 * ...
 *
 * <CanRequestConfirmDialog action="cancel"
 *   show={!!pendingCancelRequest}
 *   title={cancelConfirmTitle}
 *   message={cancelConfirmMessage}
 *   loading={!!cancellingRequestId}
 *   onConfirm={onConfirmCanRequestCancel}
 *   onClose={onCloseCancelConfirmation} />
 * ```
 *
 * @param   action Determines which confirm and dismiss labels are shown.
 * @param   show Determines whether the dialog is visible.
 * @param   title The localized dialog title.
 * @param   message The localized dialog message.
 * @param   loading Determines whether the confirm action shows loading state.
 * @param   onConfirm Invoked when the user confirms the action.
 * @param   onClose Invoked when the user dismisses or clicks the backdrop.
 * @returns The CAN request confirmation dialog component.
 */
export const CanRequestConfirmDialog = ({
  action,
  show,
  title,
  message,
  loading = false,
  onConfirm,
  onClose
}: CanRequestConfirmDialogProps) => {
  const labelKeys = ACTION_LABEL_KEYS[action];

  return (
    <UserNotificationDialog show={show}
      title={title}
      message={message}
      actionLabel={translate(labelKeys.action)}
      dismissLabel={translate(labelKeys.dismiss)}
      onAction={onConfirm}
      onDismiss={onClose}
      onBackdropClick={onClose}
      loading={loading}
      capitalize />
  );
};

export default CanRequestConfirmDialog;
